/**
 * Where you are in the app, as a value.
 *
 * Four screens do not need a routing library. What they do need is for the URL
 * to be the truth: a group's screen has to survive a hard refresh and be
 * something a person can send to somebody else, which is why the app uses the
 * History API and a path rather than component state. `public/_redirects`
 * serves the app shell for any path, so `/groups/grp_cabin` loads the SPA
 * rather than 404ing at the edge.
 *
 * **The receipt viewer has a URL of its own**, and that is not decoration. It
 * is a full-bleed overlay over the grid, which on a phone is what "a new
 * screen" means — so the back gesture has to close it. A `useState(open)`
 * lightbox takes the back gesture to mean "leave the group", which is a whole
 * screen further than anybody pressing it wanted to go. Giving it a path also
 * makes one receipt something a person can link to, which is the other half of
 * why the group screen has one.
 *
 * This half is deliberately pure — no `window`, no React — so it can be
 * tested as what it is, a function from a string to a value, and so the test
 * suite does not need a DOM to check that a group link survives a refresh.
 * The hook that reads and writes the real address bar is in `src/routes.ts`.
 */

export type Route =
  | { name: 'home' }
  | { name: 'group'; groupId: string }
  /** A group's own activity feed, and its CSV export. */
  | { name: 'activity'; groupId: string }
  /** A group's receipts: the grid, with the unfiled ones at the top. */
  | { name: 'receipts'; groupId: string }
  /**
   * What repeats in a group: the rules, and the sheet that makes one. Its own
   * screen rather than a panel on the group's, for the reason the receipts
   * pile and the activity feed have one — it is a list that keeps growing,
   * and what a rule is worth reading about (when it next falls due, what it
   * has already added) is not what the group screen is for.
   */
  | { name: 'recurring'; groupId: string }
  /**
   * One project of a group's: what is in it, and renaming, archiving and
   * filling it. A path of its own so it survives a refresh and can be sent to
   * the person who is supposed to be paying for the food.
   */
  | { name: 'project'; groupId: string; projectId: string }
  /** One receipt, full-bleed, over that grid. */
  | { name: 'receipt'; groupId: string; receiptId: string }
  /**
   * An invite link. It is the one screen a stranger can be on: whoever follows
   * it is very often not signed in and by definition not in the group yet, so
   * it sits outside `/groups/` rather than under it — a path under a group is
   * a promise that there is a group you can read, and here there is not.
   */
  | { name: 'invite'; token: string }
  /**
   * The admin console. It has a path of its own rather than a tab on the home
   * screen because it is a different job — somebody arrives at it from a
   * support conversation, with a link — and because whether it renders is a
   * fact about the account rather than about the URL: the SPA draws it for an
   * admin and refuses it for everybody else, and the server refuses it either
   * way. A route that only admins can *reach* would be a route that told
   * everybody else something by how it failed.
   */
  | { name: 'admin' }
  | { name: 'unknown'; path: string }

export function parseRoute(pathname: string): Route {
  const parts = pathname.split('/').filter(Boolean)

  if (parts.length === 0) return { name: 'home' }

  if (parts[0] === 'admin' && parts.length === 1) return { name: 'admin' }

  if (parts[0] === 'invite' && parts.length === 2) {
    return { name: 'invite', token: decodeURIComponent(parts[1]) }
  }

  if (parts[0] === 'groups' && parts.length >= 2) {
    const groupId = decodeURIComponent(parts[1])

    if (parts.length === 2) return { name: 'group', groupId }
    if (parts[2] === 'activity' && parts.length === 3) return { name: 'activity', groupId }
    if (parts[2] === 'recurring' && parts.length === 3) return { name: 'recurring', groupId }
    if (parts[2] === 'projects' && parts.length === 4) {
      return { name: 'project', groupId, projectId: decodeURIComponent(parts[3]) }
    }
    if (parts[2] === 'receipts') {
      if (parts.length === 3) return { name: 'receipts', groupId }
      if (parts.length === 4) {
        return { name: 'receipt', groupId, receiptId: decodeURIComponent(parts[3]) }
      }
    }
  }

  return { name: 'unknown', path: pathname }
}

export function pathFor(route: Route): string {
  switch (route.name) {
    case 'home':
      return '/'
    case 'group':
      return `/groups/${encodeURIComponent(route.groupId)}`
    case 'activity':
      return `/groups/${encodeURIComponent(route.groupId)}/activity`
    case 'receipts':
      return `/groups/${encodeURIComponent(route.groupId)}/receipts`
    case 'recurring':
      return `/groups/${encodeURIComponent(route.groupId)}/recurring`
    case 'project':
      return (
        `/groups/${encodeURIComponent(route.groupId)}/projects/` +
        encodeURIComponent(route.projectId)
      )
    case 'receipt':
      return (
        `/groups/${encodeURIComponent(route.groupId)}/receipts/` +
        encodeURIComponent(route.receiptId)
      )
    case 'invite':
      return `/invite/${encodeURIComponent(route.token)}`
    case 'admin':
      return '/admin'
    default:
      return route.path
  }
}

/**
 * Which *screen* a route is on.
 *
 * Not the same question as which route: it is what the shell keys the screen's
 * cross-fade off, so it has to change exactly when the thing being looked at
 * changes and not one time more. The receipt viewer is the case that makes the
 * point — it is a full-bleed overlay over the grid it came from, with a URL of
 * its own so the back gesture closes it, and it shares this key with that grid
 * precisely so that opening a receipt does not tear the grid down and rebuild
 * it behind the picture.
 */
export function screenKey(route: Route): string {
  switch (route.name) {
    case 'home':
      return 'home'
    case 'group':
      return `group:${route.groupId}`
    case 'activity':
      return `activity:${route.groupId}`
    case 'recurring':
      return `recurring:${route.groupId}`
    case 'project':
      return `project:${route.groupId}:${route.projectId}`
    case 'receipts':
    case 'receipt':
      return `receipts:${route.groupId}`
    case 'invite':
      return `invite:${route.token}`
    case 'admin':
      return 'admin'
    default:
      return 'unknown'
  }
}
